import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import { withRouter } from "../../utils/withRouter";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

class OrderDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      order: null,
      items: [],
      loading: true,
    };
  }

  async componentDidMount() {
    const { id } = this.props.router.params;
    await this.fetchOrderDetails(id);
  }

  fetchOrderDetails = async (id) => {
    try {
      const res = await axios.get(`/products/orders/${id}`);

      if (res.data.status !== "success") {
        Swal.fire("Error", "Order not found", "error");
        return;
      }

      this.setState({
        order: res.data.data[0],
        items: res.data.data.items,
        loading: false,
      });
    } catch (error) {
      Swal.fire("Error", "Failed to load order", "error");
    }
  };

  /* ===== DOWNLOAD PDF ===== */
  downloadInvoice = () => {
    const invoice = document.getElementById("invoiceArea");

    html2canvas(invoice, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`Invoice_${this.state.order.id}.pdf`);
    });
  };

  /* ===== PRINT INVOICE ===== */
  printInvoice = () => {
    const printContents = document.getElementById("invoiceArea").innerHTML;
    const originalContents = document.body.innerHTML;

    document.body.innerHTML = `
      <html>
        <head>
          <title>Print Invoice</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; }
            table, th, td { border: 1px solid #000; }
            th, td { padding: 6px; font-size: 12px; }
            .table-dark { background: #000; color: #fff; }
            .text-end { text-align: right; }
            .text-center { text-align: center; }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `;

    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  render() {
    const { order, items, loading } = this.state;

    if (loading) return <div className="p-4">Loading...</div>;

    // Safe numeric fallbacks
    const deliveryCharge = Number(order.delivery_charge || 0);
    const couponDiscount = Number(order.coupon_discount || 0);
    const discount = Number(order.discount || 0);
    const grandTotal = Number(order.grand_total || 0);
    const paidAmount = Number(order.paid_amount || 0);
    const dueAmount = Number(order.due_amount || 0);

    // Subtotal derived from final total structure
    const subtotal =
      grandTotal -
      deliveryCharge +
      couponDiscount +
      discount;

    return (
      <div className="pc-container">
        <div className="pc-content">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fw-bold">Order Details</h4>

            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={this.printInvoice}
              >
                <i className="ph ph-printer me-1"></i> Print
              </button>

              <button
                className="btn btn-primary btn-sm"
                onClick={this.downloadInvoice}
              >
                <i className="ph ph-download-simple me-1"></i>
                Download Invoice
              </button>
            </div>
          </div>

          {/* ===== INVOICE AREA ===== */}
          <div
            id="invoiceArea"
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "10px",
            }}
          >
            {/* Header */}
            <div className="d-flex justify-content-between border-bottom pb-3 mb-3">
              <div>
                <h5 className="fw-bold mb-1">INVOICE</h5>
                <div className="small text-muted">
                  Invoice No: INV-{order.id}
                </div>
                <div className="small text-muted">
                  Date: {order.order_datetime}
                </div>
              </div>

              <div className="text-end">
                <h6 className="fw-bold mb-1">Merchant</h6>
                <div className="small text-muted">
                  {order.business_name || "Your Shop Name"}
                </div>
                <div className="small text-muted">{order.phone || ""}</div>
              </div>
            </div>

            {/* Customer Info */}
            <h6 className="fw-semibold mb-2">Customer Information</h6>
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="small">
                <strong>Name:</strong> {order.full_name}
              </div>
              <div className="small">
                <strong>Phone:</strong> {order.phone}
              </div>
              {order.email && (
                <div className="small">
                  <strong>Email:</strong> {order.email}
                </div>
              )}
              <div className="small">
                <strong>Address:</strong> {order.shipping_address}
              </div>
              <div className="small">
                <strong>Division:</strong> {order.division_name}
              </div>
            </div>

            {/* Items */}
            <h6 className="fw-semibold mb-2">Order Items</h6>
            <table className="table table-bordered small mb-3">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th className="text-center">Qty</th>
                  <th className="text-end">Price</th>
                  <th className="text-end">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{it.product_name}</td>
                    <td className="text-center">{it.quantity}</td>
                    <td className="text-end">৳{it.price}</td>
                    <td className="text-end">৳{it.sub_total}</td>
                  </tr>
                ))}

                {/* Subtotal */}
                <tr>
                  <td colSpan="4" className="text-end fw-bold">
                    Subtotal
                  </td>
                  <td className="text-end">৳{subtotal.toFixed(2)}</td>
                </tr>

                {/* Charges & Discounts */}
                <tr>
                  <td colSpan="4" className="text-end fw-bold">
                    Delivery Charge
                  </td>
                  <td className="text-end">৳{deliveryCharge.toFixed(2)}</td>
                </tr>

                <tr>
                  <td colSpan="4" className="text-end fw-bold">
                    Coupon Discount
                  </td>
                  <td className="text-end">
                    − ৳{couponDiscount.toFixed(2)}
                  </td>
                </tr>

                <tr>
                  <td colSpan="4" className="text-end fw-bold">
                    Discount
                  </td>
                  <td className="text-end">
                    − ৳{discount.toFixed(2)}
                  </td>
                </tr>

                {/* Grand Total */}
                <tr className="table-dark text-white fw-bold">
                  <td colSpan="4" className="text-end">
                    Grand Total
                  </td>
                  <td className="text-end">
                    ৳{grandTotal.toFixed(2)}
                  </td>
                </tr>

                {/* Paid */}
                <tr>
                  <td colSpan="4" className="text-end fw-bold">
                    Paid Amount
                  </td>
                  <td className="text-end text-success">
                    ৳{paidAmount.toFixed(2)}
                  </td>
                </tr>

                {/* Due */}
                <tr className="table-dark text-white fw-bold">
                  <td colSpan="4" className="text-end">
                    Due Amount
                  </td>
                  <td className="text-end">
                    ৳{dueAmount.toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Notes */}
            {order.order_note && (
              <div className="mt-2 small border rounded p-2 bg-light">
                <strong>Order Note:</strong> {order.order_note}
              </div>
            )}

            <div className="text-center mt-4 small text-muted">
              Thank you for your order!
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(OrderDetails);
